/**
 * Display metadata for scopes + primitive types.
 *
 * Extracted from the retired ConfigurationView so StagedScopesList (and
 * future AP surfaces) can reuse the mappings without pulling in the full
 * configuration editor.
 */

import type { TranslationKeys } from "@/hooks/useTranslation/useTranslation"
import {
  IconAlertTriangle,
  IconPackage,
  IconPuzzle,
  IconTool,
  IconUsers,
} from "@tabler/icons-react"

export const SCOPE_ICON_COMPONENTS: Record<string, typeof IconTool> = {
  supplier: IconTool,
  component: IconPuzzle,
  product: IconPackage,
  customer: IconUsers,
  substance: IconAlertTriangle,
}

export const TYPE_COLORS: Record<string, { bg: string; text: string }> = {
  string: { bg: "bg-yellow5/20 border-yellow5", text: "text-yellow1" },
  number: { bg: "bg-success/10 border-success/30", text: "text-success" },
  date: { bg: "bg-warning/10 border-warning", text: "text-warning" },
  relation: { bg: "bg-purple1/10 border-purple1/30", text: "text-purple1" },
  formula: { bg: "bg-red1/10 border-red1/30", text: "text-red1" },
}

export const TYPE_ICONS: Record<string, string> = {
  string: "Aa",
  number: "#",
  date: "D",
  relation: "R",
  formula: "f",
}

export const SCOPE_NAME_KEYS: Record<string, TranslationKeys> = {
  supplier: "QMS/SCOPE/SUPPLIER",
  component: "QMS/SCOPE/COMPONENT",
  product: "QMS/SCOPE/PRODUCT",
  customer: "QMS/SCOPE/CUSTOMER",
  substance: "QMS/SCOPE/SUBSTANCE",
}

// Hard-coded English display names used by getScopeDisplayName /
// getPrimitiveDisplayTitle. These mirror the QMS/SCOPE/* values in
// translations_EN.ts and let scope/primitive helpers run without threading
// translate() through every caller.
const SCOPE_DISPLAY_NAMES: Record<string, string> = {
  supplier: "Suppliers",
  component: "Bill of Materials",
  product: "Products",
  customer: "Customers",
  substance: "Substances",
}

export function scopeDisplayName(
  id: string,
  translate: (k: TranslationKeys) => string,
): string {
  const key = SCOPE_NAME_KEYS[id]
  if (key) return translate(key)
  return id.charAt(0).toUpperCase() + id.slice(1)
}

// Primitive display names keyed by [scopeId][primitiveName].
// Customers ingest with German csv_headers (= PrimitiveDef.title), so EN users
// need a translation layer. DE/JA users keep the original csv_header so it
// still matches their source CSVs.
// Covers both the demo schema and the haas profile schema; primitive `name`
// values are stable per scope, so we just union the keys.
export const PRIMITIVE_DISPLAY_NAMES: Record<string, Record<string, string>> = {
  supplier: {
    supplier_id: "Supplier No.",
    supplier_name: "Supplier Name",
    name: "Supplier",
  },
  substance: {
    cas_number: "CAS No.",
    substance_name: "Substance",
  },
  component: {
    internal_id: "Part No.",
    article_number: "Part No.",
    name: "Designation",
    article_name: "Part Name",
    description: "Description",
    reach_status: "REACH Status",
    materialgruppe: "Material Group",
    svhc_gehalt: "Content (%)",
    svhc_concentration: "SVHC Content (%)",
    location_in_component: "SVHC Locations in Part",
    supplier_article_number: "Supplier Part No.",
    scip_number: "SCIP Number",
  },
  product: {
    internal_product_id: "Product No.",
    product_id: "Product No.",
    bezeichnung: "Designation",
    product_name: "Product Name",
  },
  customer: {
    customer_id: "Customer No.",
    customer_name: "Customer Name",
  },
}

export function getScopeDisplayName(
  scope: { id: string; name: string },
  language: string,
): string {
  if (language !== "EN") return scope.name
  return SCOPE_DISPLAY_NAMES[scope.id] ?? scope.name
}

export function getPrimitiveDisplayTitle(
  primitive: {
    scope_id: string
    name: string
    title: string
    type?: string
    related_scope_id?: string | null
  },
  language: string,
): string {
  if (language !== "EN") return primitive.title
  // Relation primitives carry the *related* scope's German name as their title
  // (auto-generated back-relations from bom_setup). Translate via the related
  // scope's display name so e.g. supplier's "Stückliste" relation → "Bill of
  // Materials".
  if (primitive.type === "relation" && primitive.related_scope_id) {
    return SCOPE_DISPLAY_NAMES[primitive.related_scope_id] ?? primitive.title
  }
  return (
    PRIMITIVE_DISPLAY_NAMES[primitive.scope_id]?.[primitive.name] ??
    primitive.title
  )
}
